class FlexiPincodeChecker extends HTMLElement {
  constructor() {
    super();
    this.controls = null;
    this.pincodeInput = null;
    this.pincodeSubmitBtn = null;
    this.pincodeMessage = null;
    this.sku = this.dataset.sku || '';
    this.defaultButtonLabel = 'Check';
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.controls = this.querySelector('.flexi-pincode-checker__controls');
      this.pincodeInput = this.querySelector('[name="pincode-input"]');
      this.pincodeSubmitBtn = this.querySelector('[name="pincode-submit"]');
      this.pincodeMessage = this.querySelector('[name="pincode-message"]');

      if (!this.controls || !this.pincodeInput || !this.pincodeSubmitBtn || !this.pincodeMessage || !this.sku) {
        return;
      }

      this.loadLastPincodeOrFetchFromIP();
      this.pincodeSubmitBtn.addEventListener('click', this.validatePincode.bind(this));
      this.pincodeInput.addEventListener('focus', this.moveCursorToEnd.bind(this));
      this.pincodeInput.addEventListener('input', this.handleInputChange.bind(this));
      this.pincodeInput.addEventListener('keypress', this.validateInput.bind(this));
      this.pincodeInput.addEventListener('keydown', this.handleBackspace.bind(this));
      this.pincodeInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.validatePincode();
        }
      });
    });
  }

  validatePincode() {
    const pincode = (this.pincodeInput?.value || '').trim();
    if (pincode.length !== 6) {
      this.showError('Please enter a valid pincode');
      return;
    }

    this.fetchEstimatedDeliveryFromAPI(pincode, this.sku);
  }

  async loadLastPincodeOrFetchFromIP() {
    if (!this.pincodeInput) return;

    const lastPincode = localStorage.getItem('lastPincode');
    if (lastPincode && lastPincode.length === 6) {
      this.pincodeInput.value = lastPincode;
      this.validatePincode();
      return;
    }

    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return;

      const data = await response.json();
      const ipPincode = String(data.postal || '').trim();
      if (ipPincode.length !== 6) return;

      this.pincodeInput.value = ipPincode;
      localStorage.setItem('lastPincode', ipPincode);
      this.validatePincode();
    } catch (error) {
      return;
    }
  }

  async fetchEstimatedDeliveryFromAPI(pincode, sku) {
    const apiUrl = `https://api.openleaf.tech/api/v1/custom_pincodes/closestTATUnicommerce?pincode=${pincode}&sku=${sku}`;

    try {
      this.setLoading(true);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: 'Bearer 025f23eaa925b4471e46d57cd3367b77'
        }
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      const result = await response.json();
      this.renderDeliveryMessage(result, pincode);
    } catch (error) {
      this.showError('Unable to fetch delivery info.');
    } finally {
      this.setLoading(false);
    }
  }

  renderDeliveryMessage(result, pincode) {
    const values = Array.isArray(result?.values) ? result.values : [];
    const isServiceable = values[2] === true || values[2] === 'true';

    if (!isServiceable) {
      this.showError('Delivery info not available for this pincode.');
      return;
    }

    const tatDays = parseInt(values[0], 10);
    if (Number.isNaN(tatDays)) {
      this.showError('Unable to calculate delivery date for this pincode.');
      return;
    }

    const estimatedDate = new Date();
    estimatedDate.setDate(estimatedDate.getDate() + tatDays + 1);

    const estimatedDeliveryDate = estimatedDate.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });

    const deliveryCharge = this.extractDeliveryCharge(result, values);
    const messageLines = [`<p><strong>Estimated delivery by:</strong> ${estimatedDeliveryDate}</p>`];

    if (deliveryCharge) {
      messageLines.push(`<p><strong>Delivery charge:</strong> ${this.escapeHtml(deliveryCharge)}</p>`);
    }

    this.pincodeMessage.innerHTML = messageLines.join('');
    this.pincodeMessage.classList.add('is-success');
    this.pincodeMessage.classList.remove('is-error', 'is-hidden');
    localStorage.setItem('lastPincode', pincode);
  }

  extractDeliveryCharge(result, values) {
    const candidateValues = [
      result?.delivery_charge,
      result?.deliveryCharge,
      result?.shipping_charge,
      result?.shippingCharge,
      values[3],
      values[4]
    ];

    for (const value of candidateValues) {
      if (value === null || value === undefined || value === '' || value === 'null') {
        continue;
      }

      if (typeof value === 'number') {
        return `Rs. ${value}`;
      }

      const normalizedValue = String(value).trim();
      if (!normalizedValue) {
        continue;
      }

      if (/^rs\.?/i.test(normalizedValue) || /^inr/i.test(normalizedValue) || normalizedValue.includes('₹')) {
        return normalizedValue;
      }

      if (!Number.isNaN(Number(normalizedValue))) {
        return `Rs. ${normalizedValue}`;
      }
    }

    return '';
  }

  showError(message) {
    this.pincodeMessage.textContent = message;
    this.pincodeMessage.classList.add('is-error');
    this.pincodeMessage.classList.remove('is-success', 'is-hidden');
  }

  handleInputChange() {
    if (!this.pincodeInput) return;

    this.pincodeInput.value = this.pincodeInput.value.replace(/\D/g, '').slice(0, 6);

    if (this.pincodeMessage && !this.pincodeMessage.classList.contains('is-hidden')) {
      this.pincodeMessage.classList.add('is-hidden');
      this.pincodeMessage.classList.remove('is-error', 'is-success');
      this.pincodeMessage.textContent = '';
      this.pincodeMessage.innerHTML = '';
    }
  }

  moveCursorToEnd() {
    const value = this.pincodeInput.value;
    this.pincodeInput.value = '';
    this.pincodeInput.value = value;
  }

  validateInput(event) {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleBackspace(event) {
    if (event.key === 'Backspace' && this.pincodeInput.value.length === 0) {
      event.preventDefault();
    }
  }

  setLoading(isLoading) {
    if (!this.pincodeSubmitBtn || !this.pincodeInput) return;

    this.pincodeSubmitBtn.disabled = isLoading;
    this.pincodeSubmitBtn.classList.toggle('is-loading', isLoading);
    this.pincodeSubmitBtn.textContent = isLoading ? 'Checking...' : this.defaultButtonLabel;
    this.pincodeInput.readOnly = isLoading;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

customElements.define('flexi-pincode-checker', FlexiPincodeChecker);
